import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useParams } from 'react-router-dom'
import { useGalerie } from '../../hooks/queries'
import type { Galeriebild } from '../../api/types'
import { fehlertext } from '../../api/client'
import { tagKey, tagLabel } from '../../lib/format'
import { dauerText, standbildAdresse } from '../../lib/video'
import { BackIcon, XIcon } from '../../components/Icons'
import { useEscape } from '../../hooks/useEscape'
import { VideoPlayer } from '../thread/VideoPlayer'
import { Netzstreifen } from '../../components/Netzstreifen'
import { Standbild } from '../../components/Standbild'
import { Lightbox } from '../thread/Gallery'
import { useTexte } from '../../texte'

/**
 * All pictures and videos of a trip (1.20.0) -- the category "Fotos".
 *
 * A grid by day, newest day first, in the device's clock like the chat's
 * dividers; three tiles a row on a phone, six on a tablet. A tap on a
 * picture opens the same large view as in the chat (`Lightbox`), with all
 * loaded pictures to leaf through; a tap on a video plays it in place
 * (1.20.1 -- from use: "für Videos: springt in den Chat"), with a way to
 * the post or the point below it. Until then a tap on a video went where the video
 * plays -- the chat at its message, or the map at its point -- because the
 * large view shows pictures only. Loaded in pages of a hundred and twenty,
 * one more per tap on "Mehr laden".
 *
 * **The server decides what is here.** Every tile went through
 * `Visibility` twice over -- by its message or by its point; this page
 * shows what it gets.
 */
export function FotosPage() {
  const texte = useTexte()
  const t = texte.fotos
  const { tripId: roh } = useParams()
  const tripId = Number(roh)
  const galerie = useGalerie(tripId)
  const [offen, setOffen] = useState<number | null>(null)
  /** The video playing in place (1.20.1), or none. */
  const [video, setVideo] = useState<Galeriebild | null>(null)
  useEscape(video === null ? null : () => setVideo(null))

  const alle: Galeriebild[] = galerie.data?.pages.flatMap((p) => p.media) ?? []
  const bilder = alle.filter((m) => m.kind === 'image')

  // Grouped by day, in the order the server gave (newest first).
  const tage: Array<{ key: string; eintraege: Galeriebild[] }> = []
  for (const m of alle) {
    const key = m.at === null ? '' : tagKey(m.at)
    const letzter = tage[tage.length - 1]
    if (letzter !== undefined && letzter.key === key) letzter.eintraege.push(m)
    else tage.push({ key, eintraege: [m] })
  }

  function oeffnen(m: Galeriebild) {
    if (m.kind === 'image') {
      setOffen(bilder.findIndex((b) => b.id === m.id))
      return
    }
    // A video plays in place (1.20.1); the sheet links to where it hangs.
    setVideo(m)
  }

  /** Where a video hangs: its post in the chat, or its point on the map. */
  function heimat(m: Galeriebild): { ziel: string; wort: string } | null {
    if (m.message_id !== null) return { ziel: `/t/${tripId}?m=${m.message_id}`, wort: t.zumBeitrag }
    if (m.highlight_id !== null) return { ziel: `/t/${tripId}/karte?punkt=${m.highlight_id}`, wort: t.zumPunkt }
    return null
  }

  return (
    <div className="fotos" data-fotos="">
      <Netzstreifen />
      <header className="bar">
        <Link className="icon-button" to={`/t/${tripId}/info`} aria-label={t.zurueck}>
          <BackIcon />
        </Link>
        <h2 className="bar__title bar__grow">{t.titel}</h2>
      </header>
      <div className="fotos__inhalt">
        {galerie.isError && (
          <p className="notice" role="alert">{fehlertext(galerie.error)}</p>
        )}
        {galerie.data !== undefined && alle.length === 0 && <p className="leise">{t.keine}</p>}
        {tage.map((tag) => (
          <section key={tag.key} className="fotos__tag" data-fotos-tag={tag.key}>
            <h3 className="fotos__ueberschrift">{tag.key === '' ? t.ohneTag : tagLabel(tag.key)}</h3>
            <div className="fotos__raster">
              {tag.eintraege.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="fotos__kachel"
                  onClick={() => oeffnen(m)}
                  aria-label={m.kind === 'video' ? t.videoOeffnen : t.bildOeffnen}
                  data-fotos-kind={m.kind}
                  data-fotos-id={m.id}
                >
                  <Standbild src={standbildAdresse(m) ?? m.url} loading="lazy" />
                  {m.kind === 'video' && (
                    <span className="fotos__abspielen" aria-hidden="true">
                      ▶ {m.duration_ms > 0 ? dauerText(m.duration_ms) : ''}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </section>
        ))}
        {galerie.hasNextPage && (
          <button
            type="button"
            className="button button--outline"
            disabled={galerie.isFetchingNextPage}
            onClick={() => void galerie.fetchNextPage()}
            data-fotos-mehr=""
          >
            {galerie.isFetchingNextPage ? texte.allgemein.laeuft : t.mehr}
          </button>
        )}
      </div>

      {/* The video in place (1.20.1): the same dark stage as the large
          view, the chat's player in the middle, the way to its post below.
          A portal at `document.body` like the large view -- the page's
          frosted material would otherwise hold a `position: fixed` box. */}
      {video !== null &&
        createPortal(
          <div className="lightbox fotos__video" role="dialog" aria-modal="true" aria-label={t.videoTitel} data-fotos-video="">
            <div className="lightbox__kopf">
              <button type="button" className="lightbox__close" onClick={() => setVideo(null)} aria-label={texte.allgemein.schliessen}>
                <XIcon size={22} />
              </button>
            </div>
            <div className="lightbox__flaeche" onClick={() => setVideo(null)}>
              <div className="fotos__videokasten" onClick={(event) => event.stopPropagation()}>
                <VideoPlayer media={video} />
              </div>
            </div>
            {heimat(video) !== null && (
              <div className="lightbox__bar">
                <Link className="button button--outline" to={heimat(video)!.ziel} data-fotos-heimat="">
                  {heimat(video)!.wort}
                </Link>
              </div>
            )}
          </div>,
          document.body,
        )}

      {offen !== null && bilder[offen] !== undefined && (
        <Lightbox
          media={bilder}
          start={offen}
          reactionsFor={() => []}
          canReact={false}
          onReact={() => undefined}
          onClose={() => setOffen(null)}
        />
      )}
    </div>
  )
}
